/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * MovingWorlds API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type {
  HTTPValidationError,
  PostDiscoveryUpdateBody,
  SendNewIntroduction200,
  SrcRoutersMatchesNewIntroductionPayload,
  UpdateIntroductionOutcomeBody,
  UpdateMatchDecisionResponse,
} from './platformapi.schemas';

import postDiscoveryUpdateMutator from '../custom-axios-instance';
import sendNewIntroductionMutator from '../custom-axios-instance';
import updateIntroductionOutcomeMutator from '../custom-axios-instance';

/**
 * Handles a discovery update for a given match group. The update can be from either a professional or a representative and includes details such as the outcome, scheduled call date, and additional notes. If the discovery is a decision (matched or declined), it will also register a decision update and progress the match if accepted by relevant party.
 * @summary Post Discovery Update
 */
export const postDiscoveryUpdate = (
  matchGroupId: number,
  postDiscoveryUpdateBody: PostDiscoveryUpdateBody,
  signal?: AbortSignal,
) => {
  return postDiscoveryUpdateMutator<unknown>({
    url: `${import.meta.env['VITE_API_HOST']}/matches/discovery_update/${matchGroupId}`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postDiscoveryUpdateBody,
    signal,
  });
};

export const getPostDiscoveryUpdateMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDiscoveryUpdate>>,
    TError,
    { matchGroupId: number; data: PostDiscoveryUpdateBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDiscoveryUpdate>>,
  TError,
  { matchGroupId: number; data: PostDiscoveryUpdateBody },
  TContext
> => {
  const mutationKey = ['postDiscoveryUpdate'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDiscoveryUpdate>>,
    { matchGroupId: number; data: PostDiscoveryUpdateBody }
  > = (props) => {
    const { matchGroupId, data } = props ?? {};

    return postDiscoveryUpdate(matchGroupId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDiscoveryUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof postDiscoveryUpdate>>
>;
export type PostDiscoveryUpdateMutationBody = PostDiscoveryUpdateBody;
export type PostDiscoveryUpdateMutationError = void | HTTPValidationError;

/**
 * @summary Post Discovery Update
 */
export const usePostDiscoveryUpdate = <TError = void | HTTPValidationError, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postDiscoveryUpdate>>,
      TError,
      { matchGroupId: number; data: PostDiscoveryUpdateBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postDiscoveryUpdate>>,
  TError,
  { matchGroupId: number; data: PostDiscoveryUpdateBody },
  TContext
> => {
  const mutationOptions = getPostDiscoveryUpdateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sends an introduction from the current user to the target user.
 * @summary Send New Introduction
 */
export const sendNewIntroduction = (
  srcRoutersMatchesNewIntroductionPayload: SrcRoutersMatchesNewIntroductionPayload,
  signal?: AbortSignal,
) => {
  return sendNewIntroductionMutator<SendNewIntroduction200>({
    url: `${import.meta.env['VITE_API_HOST']}/matches/send_new_introduction`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: srcRoutersMatchesNewIntroductionPayload,
    signal,
  });
};

export const getSendNewIntroductionMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendNewIntroduction>>,
    TError,
    { data: SrcRoutersMatchesNewIntroductionPayload },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendNewIntroduction>>,
  TError,
  { data: SrcRoutersMatchesNewIntroductionPayload },
  TContext
> => {
  const mutationKey = ['sendNewIntroduction'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendNewIntroduction>>,
    { data: SrcRoutersMatchesNewIntroductionPayload }
  > = (props) => {
    const { data } = props ?? {};

    return sendNewIntroduction(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendNewIntroductionMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendNewIntroduction>>
>;
export type SendNewIntroductionMutationBody = SrcRoutersMatchesNewIntroductionPayload;
export type SendNewIntroductionMutationError = void | HTTPValidationError;

/**
 * @summary Send New Introduction
 */
export const useSendNewIntroduction = <TError = void | HTTPValidationError, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendNewIntroduction>>,
      TError,
      { data: SrcRoutersMatchesNewIntroductionPayload },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof sendNewIntroduction>>,
  TError,
  { data: SrcRoutersMatchesNewIntroductionPayload },
  TContext
> => {
  const mutationOptions = getSendNewIntroductionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * API to accept or decline a pending introduction
 * @summary Update Introduction Outcome
 */
export const updateIntroductionOutcome = (
  matchGroupId: number,
  updateIntroductionOutcomeBody: UpdateIntroductionOutcomeBody,
  signal?: AbortSignal,
) => {
  return updateIntroductionOutcomeMutator<UpdateMatchDecisionResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/matches/update_introduction_outcome/${matchGroupId}`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: updateIntroductionOutcomeBody,
    signal,
  });
};

export const getUpdateIntroductionOutcomeMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateIntroductionOutcome>>,
    TError,
    { matchGroupId: number; data: UpdateIntroductionOutcomeBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateIntroductionOutcome>>,
  TError,
  { matchGroupId: number; data: UpdateIntroductionOutcomeBody },
  TContext
> => {
  const mutationKey = ['updateIntroductionOutcome'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateIntroductionOutcome>>,
    { matchGroupId: number; data: UpdateIntroductionOutcomeBody }
  > = (props) => {
    const { matchGroupId, data } = props ?? {};

    return updateIntroductionOutcome(matchGroupId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateIntroductionOutcomeMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateIntroductionOutcome>>
>;
export type UpdateIntroductionOutcomeMutationBody = UpdateIntroductionOutcomeBody;
export type UpdateIntroductionOutcomeMutationError = void | HTTPValidationError;

/**
 * @summary Update Introduction Outcome
 */
export const useUpdateIntroductionOutcome = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateIntroductionOutcome>>,
      TError,
      { matchGroupId: number; data: UpdateIntroductionOutcomeBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateIntroductionOutcome>>,
  TError,
  { matchGroupId: number; data: UpdateIntroductionOutcomeBody },
  TContext
> => {
  const mutationOptions = getUpdateIntroductionOutcomeMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
